import { useRef, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useDrafts } from '../hooks/useDrafts';
import { useClients } from '../hooks/useClients';
import { loadBrandColors, getClientColor } from '../utils/clientColor';
import './SideNav.css';

const NAV_TOP = [
  { to: '/dashboard', icon: '🏠', label: 'Inicio', end: true },
];

const NAV_BOTTOM = [
  { to: '/para-aprobar',  icon: '✅', label: 'Para aprobar', badge: true },
  { to: '/buzon',         icon: '📥', label: 'Buzón' },
  { to: '/conexiones',    icon: '🔌', label: 'Conexiones' },
  { to: '/supercomputer', icon: '✦',  label: 'Supercomputer' },
  { to: '/plantillas',    icon: '⚡', label: 'Herramientas IA' },
  { to: '/analytics',     icon: '📊', label: 'Analítica' },
  { to: '/settings',      icon: '⚙️',  label: 'Ajustes' },
];

const ROUTE_ALIASES = {
  '/para-aprobar': '/dashboard',
};

export default function SideNav() {
  const { currentUser, logout } = useAuth();
  const { subscription } = useSubscription();
  const { drafts: pendingDrafts } = useDrafts({ estado: 'pendiente_revision' });
  const pendingCount = pendingDrafts.length;
  const clients = useClients();
  const [waActive, setWaActive] = useState(false);
  const [autoActive, setAutoActive] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const API = process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub-2.onrender.com';
    // Account-level WhatsApp connection — same source ClientWorkspace/Settings
    // use (whatsapp_accounts via the backend), not the legacy Firestore doc.
    currentUser.getIdToken()
      .then(token => fetch(`${API}/api/agent/account-status`, { headers: { Authorization: `Bearer ${token}` } }))
      .then(r => r.ok ? r.json() : { config: null })
      .then(d => setWaActive(!!d.config?.enabled))
      .catch(() => {});

    currentUser.getIdToken()
      .then(token => fetch(`${API}/api/automations/list`, { headers: { Authorization: `Bearer ${token}` } }))
      .then(r => r.ok ? r.json() : { automations: [] })
      .then(d => setAutoActive((d.automations || []).some(a => a.status === 'active')))
      .catch(() => {});
  }, [currentUser?.uid]); // eslint-disable-line

  const location = useLocation();
  const navigate  = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(true);

  const firstName = currentUser?.displayName?.split(' ')[0]
    || (() => {
        const local = currentUser?.email?.split('@')?.[0] || '';
        const part  = local.split('.')[0];
        return part ? part.charAt(0).toUpperCase() + part.slice(1) : '';
      })();

  function isActive(item) {
    const target = ROUTE_ALIASES[item.to] || item.to;
    const current = location.pathname;
    if (item.end) return current === target || current === item.to;
    return current.startsWith(target) || current.startsWith(item.to);
  }

  function handleNav(item) {
    const alias = ROUTE_ALIASES[item.to];
    if (alias) {
      navigate(alias, { state: { section: item.to.replace('/', '') } });
    } else {
      navigate(item.to);
    }
  }

  // Current plan ids are grow/scale/evolution (+ admin); pro/enterprise/starter
  // are legacy. Unknown ids used to fall back to "Gratis" — even for admins.
  const planLabel = {
    free: 'Gratis', grow: 'Grow', scale: 'Scale', evolution: 'Evolution', admin: 'Admin',
    starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise',
  }[subscription] || 'Gratis';

  function renderNavItem(item) {
    const active = isActive(item);
    return (
      <button
        key={item.to}
        className={`snav__item${active ? ' snav__item--active' : ''}`}
        onClick={() => handleNav(item)}
        aria-current={active ? 'page' : undefined}
      >
        <span className="snav__item-icon">{item.icon}</span>
        {!collapsed && <span className="snav__item-label">{item.label}</span>}
        {item.badge && pendingCount > 0 && (
          <span className="snav__badge">{pendingCount > 99 ? '99+' : pendingCount}</span>
        )}
      </button>
    );
  }

  const onClientRoute = location.pathname.startsWith('/client/');

  return (
    <aside className={`snav${collapsed ? ' snav--collapsed' : ''}`} aria-label="Navegación principal">
      {/* Logo */}
      <div className="snav__logo">
        <div className="snav__logo-mark">G</div>
        {!collapsed && <span className="snav__logo-name">Gormaran</span>}
        <button
          className="snav__collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      {/* Nav */}
      <nav className="snav__nav">
        {NAV_TOP.map(renderNavItem)}

        {/* Marcas — expandable client list */}
        <div className="snav__section">
          <button
            className={`snav__section-hdr snav__item${onClientRoute ? ' snav__item--active' : ''}`}
            onClick={() => setClientsOpen(o => !o)}
            title={collapsed ? 'Marcas' : undefined}
          >
            <span className="snav__item-icon">🏷</span>
            {!collapsed && (
              <>
                <span className="snav__item-label">
                  Marcas{clients.length > 0 ? ` (${clients.length})` : ''}
                </span>
                <span className="snav__section-chevron">{clientsOpen ? '▾' : '▸'}</span>
              </>
            )}
          </button>

          {clientsOpen && (
            <div className="snav__client-list">
              {clients.map((c) => {
                const color = getClientColor(c, loadBrandColors());
                const name = c.name || c.company || 'Sin nombre';
                const active = location.pathname === `/client/${c.id}`;
                return (
                  <button
                    key={c.id}
                    className={`snav__client-item${active ? ' snav__client-item--active' : ''}`}
                    onClick={() => navigate(`/client/${c.id}`)}
                    title={collapsed ? name : undefined}
                  >
                    <span className="snav__client-dot" style={{ background: color }} />
                    {!collapsed && <span className="snav__client-name">{name}</span>}
                  </button>
                );
              })}
              {!collapsed && (
                <button
                  className="snav__client-add"
                  onClick={() => navigate('/dashboard', { state: { section: 'marcas' } })}
                >
                  + Nueva marca
                </button>
              )}
            </div>
          )}
        </div>

        {NAV_BOTTOM.map(renderNavItem)}

        {/* Automatizaciones — same pattern as WhatsApp below, orange pulsing dot when at least one flow is active */}
        <button
          className={`snav__item snav__item--auto${location.pathname === '/automations' ? ' snav__item--active' : ''}`}
          onClick={() => navigate('/automations')}
          title={collapsed ? 'Automatizaciones' : undefined}
        >
          <span className="snav__item-icon">⚡</span>
          {!collapsed && <span className="snav__item-label">Automatizaciones</span>}
          {autoActive && <span className="snav__auto-dot" />}
        </button>

        {/* WhatsApp — always shown; pulsing green dot when active, dim when not registered */}
        <button
          className={`snav__item snav__item--wa${!waActive ? ' snav__item--wa-inactive' : ''}${location.pathname === '/settings' ? ' snav__item--active' : ''}`}
          onClick={() => navigate('/settings#whatsapp')}
          title={collapsed ? (waActive ? 'WhatsApp activo' : 'Activar WhatsApp') : undefined}
        >
          <span className="snav__item-icon">💬</span>
          {!collapsed && (
            <span className="snav__item-label">
              WhatsApp{!waActive && <span className="snav__wa-tag">Activar</span>}
            </span>
          )}
          {waActive && <span className="snav__wa-dot" />}
        </button>
      </nav>

      {/* Account */}
      <div className="snav__footer">
        <button className="snav__account" onClick={() => navigate('/settings')}>
          <div className="snav__avatar">
            {firstName ? firstName.charAt(0).toUpperCase() : '?'}
          </div>
          {!collapsed && (
            <div className="snav__account-info">
              <span className="snav__account-name">{firstName || currentUser?.email}</span>
              <span className="snav__account-plan">{planLabel}</span>
            </div>
          )}
        </button>
        {!collapsed && (
          <button className="snav__logout" onClick={logout} aria-label="Cerrar sesión">
            ↩
          </button>
        )}
      </div>
    </aside>
  );
}
